import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CheckboxControlComponent } from '../skidchecksheet/components/controls/checkbox-control.component';
import { DatepickerControlComponent } from '../skidchecksheet/components/controls/datepicker-control.component';
import { DatetimepickerControlComponent } from '../skidchecksheet/components/controls/datetimepicker-control.component';
import { DecimalControlComponent } from '../skidchecksheet/components/controls/decimal-control.component';
import { DigitalsignatureControlComponent } from '../skidchecksheet/components/controls/digitalsignature-control.component';
import { IntegerControlComponent } from '../skidchecksheet/components/controls/integer-control.component';
import { LabeltextControlComponent } from '../skidchecksheet/components/controls/labeltext-control.component';
import { PictureControlComponent } from '../skidchecksheet/components/controls/picture-control.component';
import { QrscannerControlComponent } from '../skidchecksheet/components/controls/qrscanner-control.component';
import { RichtextControlComponent } from '../skidchecksheet/components/controls/richtext-control.component';
import { SelectControlComponent } from '../skidchecksheet/components/controls/select-control.component';
import { SignatureControlComponent } from '../skidchecksheet/components/controls/signature-control.component';
import { SkidsControlComponent } from '../skidchecksheet/components/controls/skids-control.component';
import { TableControlComponent } from '../skidchecksheet/components/controls/table-control.component';
import { TextareaControlComponent } from '../skidchecksheet/components/controls/textarea-control.component';
import { TextboxControlComponent } from '../skidchecksheet/components/controls/textbox-control.component';
import { WeatherControlComponent } from '../skidchecksheet/components/controls/weather-control.component';
import { AttachmentControlComponent } from '../skidchecksheet/components/controls/attachment-control.component';
import { ControlType, LineItemModel, SectionChildItem, TemplateModel, TemplateSectionModel } from '../../models/template.model';
import { TemplateService } from '../../services/template.service';

declare global {
  interface Window {
    __KANSEI_EXPORT_READY__?: boolean;
  }
}

@Component({
  selector: 'app-template-preview-print',
  standalone: true,
  imports: [
    CommonModule,
    TextboxControlComponent,
    TextareaControlComponent,
    CheckboxControlComponent,
    SelectControlComponent,
    DatepickerControlComponent,
    DatetimepickerControlComponent,
    TableControlComponent,
    AttachmentControlComponent,
    PictureControlComponent,
    SignatureControlComponent,
    DigitalsignatureControlComponent,
    QrscannerControlComponent,
    WeatherControlComponent,
    RichtextControlComponent,
    LabeltextControlComponent,
    SkidsControlComponent,
    DecimalControlComponent,
    IntegerControlComponent,
  ],
  templateUrl: './template-preview-print.component.html',
  styleUrl: './template-preview-print.component.css',
})
export class TemplatePreviewPrintComponent implements OnInit {
  readonly ControlType = ControlType;
  template: TemplateModel = {} as TemplateModel;
  sections: TemplateSectionModel[] = [];
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private templateService: TemplateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    window.__KANSEI_EXPORT_READY__ = false;

    const templateId = Number(this.route.snapshot.paramMap.get('templateId'));
    const exportToken = this.route.snapshot.queryParamMap.get('exportToken') || '';

    if (!templateId || !exportToken) {
      this.errorMessage = 'Export token is missing.';
      this.markReady();
      return;
    }

    this.templateService.getTemplatePreviewExportData(templateId, exportToken).subscribe({
      next: ({ template, sections }) => {
        this.template = template;
        this.sections = this.buildHierarchy(sections || []);
        this.sortLineItems(this.sections);
        this.cdr.markForCheck();
        setTimeout(() => this.markReady(), 300);
      },
      error: () => {
        this.errorMessage = 'Unable to load export data.';
        this.markReady();
      }
    });
  }

  getSectionChildren(section: TemplateSectionModel): SectionChildItem[] {
    const children: SectionChildItem[] = [];

    section.lineItemList?.forEach(item => {
      children.push({ type: 'lineItem', data: item, sortOrder: item.sortOrder || 0 });
    });

    section.subsections?.forEach(subsection => {
      children.push({ type: 'subsection', data: subsection, sortOrder: subsection.sortOrder || 0 });
    });

    return children.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  asLineItem(child: SectionChildItem): LineItemModel {
    return child.data as LineItemModel;
  }

  asSubsection(child: SectionChildItem): TemplateSectionModel {
    return child.data as TemplateSectionModel;
  }

  private buildHierarchy(flatSections: TemplateSectionModel[]): TemplateSectionModel[] {
    const sectionMap = new Map<number, TemplateSectionModel>();
    const rootSections: TemplateSectionModel[] = [];

    flatSections.forEach(section => {
      sectionMap.set(section.sectionId!, { ...section, subsections: [], isExpanded: true });
    });

    flatSections.forEach(section => {
      const sectionNode = sectionMap.get(section.sectionId!);
      if (!sectionNode) {
        return;
      }

      if (section.parentSectionId === null || section.parentSectionId === undefined) {
        rootSections.push(sectionNode);
        return;
      }

      const parent = sectionMap.get(section.parentSectionId);
      if (parent) {
        parent.subsections = parent.subsections || [];
        parent.subsections.push(sectionNode);
      } else {
        rootSections.push(sectionNode);
      }
    });

    this.sortSections(rootSections);
    return rootSections;
  }

  private sortSections(sections: TemplateSectionModel[]): void {
    sections.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    sections.forEach(section => this.sortSections(section.subsections || []));
  }

  private sortLineItems(sections: TemplateSectionModel[]): void {
    sections.forEach(section => {
      section.lineItemList?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      this.sortLineItems(section.subsections || []);
    });
  }

  private markReady(): void {
    window.__KANSEI_EXPORT_READY__ = true;
  }
}
